import { useEffect, useState } from 'react';
import type { FC, ElementType } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Divider, Drawer, List } from '@material-ui/core';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import GroupsIcon from '@material-ui/icons/Groups';
import StoreIcon from '@material-ui/icons/Store';
import PersonIcon from '@material-ui/icons/Person';
import PublicIcon from '@material-ui/icons/Public';
import { useSettings } from 'src/contexts/settings-context';
import { Leaf } from 'src/icons/leaf';
import { DashboardSidebarItem } from './dashboard-sidebar-item';
import { Scrollbar } from './scrollbar';
import { ShoppingBag as ShoppingBagIcon } from '../icons/shopping-bag';
import { CustomUsers as UsersIcon } from '../icons/custom-users';
import { ReceiptTax as ReceiptTaxIcon } from '../icons/receipt-tax';
import { Roles } from '../enums/role-based-auth-guard';
import { useAuth } from '../hooks/use-auth';
import { AccountPopover } from './account-popover';

interface DashboardSidebarProps {
  onPin: () => void;
  pinned: boolean;
}

interface Item {
  icon: ElementType;
  title: string;
  href?: string;
  external?: boolean;
  accessTo?: Array<string>;
  identifier: string;
  items?: Array<{ href: string; title: string; }>
}

const items: Item[] = [
  {
    icon: EqualizerIcon,
    title: 'Dashboard',
    href: '/dashboard',
    accessTo: [Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER, Roles.TECHNICIAN, Roles.DEALER],
    identifier: 'dashboard'
    // Add items: [] if necessary
  },
  {
    icon: ShoppingBagIcon,
    title: 'Masters',
    href: '/dashboard/masters',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'masters',
    items: [
      {
        href: '/dashboard/masters/base/category',
        title: 'Base Masters'
      },
      {
        href: '/dashboard/masters/model/all',
        title: 'Model Master'
      },
      {
        href: '/dashboard/masters/spare-part/main/all',
        title: 'Spare Part Master'
      },
      {
        href: '/dashboard/masters/price/service/all',
        title: 'Price Master'
      },
      {
        href: '/dashboard/masters/issue/all',
        title: 'Issue Master'
      },
      {
        href: '/dashboard/masters/repairService',
        title: 'Repair Service Master'
      },
      {
        href: '/dashboard/masters/general/uom',
        title: 'General Master'
      },
      {
        href: '/dashboard/masters/eWasteService',
        title: 'E Waste Service Master'
      }
    ]
  },
  {
    icon: GroupsIcon,
    title: 'Channel Partners',
    href: '/dashboard/channel-partners/main/invited',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'channel-partners',
    items: [
      {
        href: '/dashboard/channel-partners/main/invited',
        title: 'Invited'
      },
      {
        href: '/dashboard/channel-partners/main/verified',
        title: 'Verified'
      },
      {
        href: '/dashboard/channel-partners/main/incomplete',
        title: 'Incomplete'
      }
    ]
  },
  {
    icon: ShoppingBagIcon,
    title: 'My Profile',
    href: '/dashboard/my-profile',
    accessTo: [Roles.CHANNEL_PARTNER],
    identifier: 'my-profile'
    // Add items: [] if necessary
  },
  {
    icon: PersonIcon,
    title: 'My Profile',
    href: '/dashboard/my-profile-dealer',
    accessTo: [Roles.DEALER],
    identifier: 'my-profile-dealer'
  },
  {
    icon: ReceiptTaxIcon,
    title: 'Technicians',
    href: '/dashboard/technicians/main/pending',
    accessTo: [Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER],
    identifier: 'technicians',
    items: [
      {
        href: '/dashboard/technicians/main/pending',
        title: 'Pending'
      },
      {
        href: '/dashboard/technicians/main/verified',
        title: 'Verified'
      },
      {
        href: '/dashboard/technicians/main/incomplete',
        title: 'Incomplete'
      }
    ]
  },
  {
    icon: UsersIcon,
    title: 'Customers',
    href: '/dashboard/customers/search',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'customers',
    items: [
      {
        href: '/dashboard/customers/search',
        title: 'Search'
      },
      {
        href: '/dashboard/customers/delete',
        title: 'Delete'
      },
    ]
  },
  {
    icon: StoreIcon,
    title: 'Dealers',
    href: '/dashboard/dealers',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'dealers',
  },
  {
    icon: LocalAtmIcon,
    title: 'Payments',
    href: '/dashboard/payments',
    accessTo: [Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER],
    identifier: 'payments'
    // Add items: [] if necessary
  },
  {
    icon: Leaf,
    title: 'Reward Points',
    href: '/dashboard/reward-points',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'reward-points'
    // Add items: [] if necessary
  },
  {
    icon: HeadsetMicIcon,
    title: 'Escalations',
    href: '/dashboard/escalations/',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'escalations'
    // Add items: [] if necessary
  },
  {
    icon: PublicIcon,
    title: 'Web',
    href: '#',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'web',
    items: [
      {
        href: '/dashboard/statistics',
        title: 'Statistics',
      },
      {
        href: '/dashboard/ewaste-drives',
        title: 'E Waste Drives',
      },
    ],
  },
];

export const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
  const { onPin, pinned } = props;
  const { pathname } = useLocation();
  const [openedItem, setOpenedItem] = useState<Item | null>(null);
  const [activeItem, setActiveItem] = useState<Item | null>(null);
  const [activeHref, setActiveHref] = useState('');
  const [hovered, setHovered] = useState(false);
  const auth = useAuth();

  const handleOpenItem = (item: Item): void => {
    if (openedItem === item) {
      setOpenedItem(null);
      return;
    }

    setOpenedItem(item);
  };
  const { settings, saveSettings } = useSettings();
  const [darkMode, setDarkMode] = useState(settings.theme === 'dark');

  const handleSwitchTheme = (): void => {
    saveSettings({
      ...settings,
      theme: settings.theme === 'light' ? 'dark' : 'light'
    });

    setDarkMode(settings.theme === 'light');
  };

  useEffect(() => {
    items.forEach((item) => {
      // set the sidebar mail element active
      const active = pathname.toLocaleLowerCase().includes(item.identifier);
      if (active) {
        setActiveItem(item);
        setActiveHref(item.href);
        // setOpenedItem(item);
      }
    });
  }, [pathname]);

  return (
    <Drawer
      open
      sx={{ zIndex: 1000 }}
      variant="permanent"
      PaperProps={{
        onMouseOver: () => { setHovered(true); },
        onMouseLeave: () => { setHovered(false); },
        sx: {
          backgroundColor: 'primary.dark',
          height: '100%',
          overflowX: 'hidden',
          transition: 'width 250ms ease-in-out',
          width: pinned ? 250 : 73,
          '& .simplebar-content': {
            height: '100%'
          },
          '&:hover': {
            width: 250,
            '& span, p': {
              display: 'flex'
            }
          }
        }
      }}
    >
      <Scrollbar
        style={{
          display: 'flex',
          flex: 1,
          overflowX: 'hidden',
          overflowY: 'auto'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 2
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              mt: 1,
              height: 40
            }}
          >
            <img
              src="/servcare-logo-horizontal.png"
              alt="ServCare Logo"
              style={{
                width: 'auto',
                height: '100%',
                maxWidth: (hovered || pinned) ? '100%' : '40px',
              }}
            />
          </Box>
          <Divider sx={{ mt: 2, mb: 2, width: 0 }} />
          <List disablePadding>
            {(
              items.map((item) => (item?.accessTo?.includes(auth.user.scope)) && (
                <DashboardSidebarItem
                  active={activeItem?.title === item.title}
                  activeHref={activeHref}
                  key={item.title}
                  onOpen={() => handleOpenItem(item)}
                  open={openedItem?.title === item.title && (hovered || pinned)}
                  pinned={pinned}
                  {...item}
                  isMaster={item.identifier === 'masters'}
                />
              ))
            )}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Divider sx={{ mb: 2 }} />
          <AccountPopover
            darkMode={darkMode}
            onSwitchTheme={handleSwitchTheme}
            showName={hovered || pinned}
            onPin={onPin}
            pinned={pinned}
          />
        </Box>
      </Scrollbar>
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onPin: PropTypes.func,
  pinned: PropTypes.bool
};
